import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Check, RefreshCw, ShieldCheck } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import { ToastProvider, useToast, apiGet, adminMutate, usePermissions } from '../admin/primitives.js'
import { estErreurApi, fraicheurFr, nombreFr } from './lib.js'
import { executerCommande } from './AcquisitionPages.js'

/**
 * INTÉGRATION TRACKER CLUB — insta.liveclubrennes.com.
 *
 * Les jetons vont AU COFFRE et n'en ressortent jamais : l'écran dit
 * « Configurée », pas la valeur. Le test de connexion est en LECTURE SEULE
 * (§83) — le jeton d'administration se prouve lors d'une vraie action.
 */

interface EtatIntegration {
  service: string
  api_version: string
  schema_version: number
  read_configured: boolean
  write_configured: boolean
  health: { status: string; api_version: string | null; schema_version: number | null; checked_at: string | null; detail: string | null }
  last_sync_at: string | null
  last_sync_ok_at: string | null
  last_links_sync_at: string | null
  last_error: string | null
  cursor: number
  last_bilan: Record<string, unknown> | null
  reconciliation: { at: string | null; provider_clicks: number | null; local_clicks: number | null; provider_visitors: number | null; local_visitors: number | null; match: boolean | null }
  local_events: number
  local_links: number
  local_bots: number
}

function LigneEtat({ label, children }: { label: string; children: ReactNode }) {
  return <li><span className="cm-actionlist__label">{label}</span><strong>{children}</strong></li>
}

function Contenu() {
  const toast = useToast()
  const [data, setData] = useState<EtatIntegration | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [lecture, setLecture] = useState('')
  const [admin, setAdmin] = useState('')
  const [busy, setBusy] = useState<'test' | 'sync' | 'save' | null>(null)
  const [testResultat, setTestResultat] = useState<Record<string, unknown> | null>(null)
  const [syncBilan, setSyncBilan] = useState<Record<string, unknown> | null>(null)
  const permissions = usePermissions()
  const gere = permissions.permissions.includes('communication.settings.manage')

  const charger = useCallback(() => {
    void apiGet<EtatIntegration | { error: string; message?: string }>('/api/v1/communication/acquisition/integration')
      .then((j) => { if (estErreurApi(j)) setErreur(j.message ?? 'Lecture impossible.'); else { setErreur(null); setData(j) } })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [])
  useEffect(() => { charger() }, [charger])

  const sante = data?.health
  const pillSante = !sante?.checked_at ? <span className="lc-pill lc-pill--idle">jamais contacté</span>
    : sante.status === 'OK' ? <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />opérationnel</span>
    : sante.status === 'VERSION_INCOMPATIBLE' ? <span className="lc-pill lc-pill--warning">version incompatible</span>
    : <span className="lc-pill lc-pill--warning">injoignable</span>

  return (
    <>
      <header className="cm-head">
        <div>
          <h1 className="lc-title">Tracker Club</h1>
          <p className="lc-subtitle">
            insta.liveclubrennes.com · API v1 · schéma 3 — les supports QR, NFC
            et liens qui amènent vers Instagram. {pillSante}
          </p>
        </div>
        <div className="cm-head__actions">
          <Link href="/communication/liens" className="lc-btn">Liens &amp; QR/NFC</Link>
        </div>
      </header>
      {erreur ? <p className="lc-hint">{erreur}</p> : null}
      {sante?.status === 'VERSION_INCOMPATIBLE' ? (
        <p className="cm-safenote">{sante.detail}</p>
      ) : null}

      <div className="acq-deuxcol">
        <section className="lc-panel acq-panel">
          <header className="lc-panel__head"><h2 className="lc-section-title">État</h2></header>
          <div className="lc-panel__body">
            <ul className="cm-actionlist">
              <LigneEtat label="Service">{data?.service ?? '…'}</LigneEtat>
              <LigneEtat label="API">{sante?.api_version ?? 'v1'} · schéma {sante?.schema_version ?? 3}</LigneEtat>
              <LigneEtat label="Lecture">{data?.read_configured ? <span className="acq-ok"><Check size={13} aria-hidden="true" /> Configurée</span> : 'Non configurée'}</LigneEtat>
              <LigneEtat label="Administration">{data?.write_configured ? <span className="acq-ok"><Check size={13} aria-hidden="true" /> Configurée</span> : 'Non configurée'}</LigneEtat>
              <LigneEtat label="Dernière synchronisation">{data?.last_sync_ok_at ? fraicheurFr(data.last_sync_ok_at) : 'jamais'}</LigneEtat>
              <LigneEtat label="Dernier curseur">{data ? String(data.cursor) : '…'}</LigneEtat>
              <LigneEtat label="Événements locaux">{data ? nombreFr(data.local_events) : '…'}</LigneEtat>
              <LigneEtat label="Robots écartés">{data ? nombreFr(data.local_bots) : '…'}</LigneEtat>
              <LigneEtat label="Liens en miroir">{data ? nombreFr(data.local_links) : '…'}</LigneEtat>
            </ul>
            {data?.reconciliation.at ? (
              <p className={`lc-hint ${data.reconciliation.match ? '' : 'acq-mismatch'}`}>
                Réconciliation {fraicheurFr(data.reconciliation.at)} :
                tracker {nombreFr(data.reconciliation.provider_clicks ?? 0)} clics / {nombreFr(data.reconciliation.provider_visitors ?? 0)} uniques ·
                Gestion {nombreFr(data.reconciliation.local_clicks ?? 0)} / {nombreFr(data.reconciliation.local_visitors ?? 0)} —
                {data.reconciliation.match ? ' écart 0 ✓' : ' ÉCART DÉTECTÉ (voir Centre d’activité)'}
              </p>
            ) : null}
          </div>
        </section>

        <section className="lc-panel acq-panel">
          <header className="lc-panel__head"><h2 className="lc-section-title">Jetons</h2>
            <span className="lc-hint">chiffrés au coffre — jamais réaffichés</span></header>
          <div className="lc-panel__body">
            {gere ? (
              <form className="acq-form" onSubmit={async (e) => {
                e.preventDefault()
                setBusy('save')
                const body: Record<string, string> = {}
                if (lecture.trim()) body['read_token'] = lecture.trim()
                if (admin.trim()) body['write_token'] = admin.trim()
                const r = await adminMutate<{ error?: string; message?: string; ok?: boolean }>(
                  'PUT', '/api/v1/communication/acquisition/integration/tokens', body)
                setBusy(null)
                if (r.status === 200) {
                  toast('ok', 'Jetons enregistrés au coffre.')
                  setLecture(''); setAdmin(''); charger()
                } else toast('error', r.data.message ?? 'Enregistrement refusé.')
              }}>
                <label className="acq-champ">
                  <span>Jeton de lecture</span>
                  <input className="lc-input" type="password" name="tracker-lecture" autoComplete="new-password"
                    value={lecture} placeholder={data?.read_configured ? '••••••••  (déjà configuré)' : 'TRACKER_API_READ_TOKEN'}
                    onChange={(e) => setLecture(e.target.value)} />
                </label>
                <label className="acq-champ">
                  <span>Jeton d’administration</span>
                  <input className="lc-input" type="password" name="tracker-admin" autoComplete="new-password"
                    value={admin} placeholder={data?.write_configured ? '••••••••  (déjà configuré)' : 'TRACKER_API_WRITE_TOKEN'}
                    onChange={(e) => setAdmin(e.target.value)} />
                </label>
                <button type="submit" className="lc-btn lc-btn--primary" disabled={busy === 'save' || (!lecture.trim() && !admin.trim())}>
                  <ShieldCheck size={14} aria-hidden="true" /> {busy === 'save' ? 'Enregistrement…' : 'Enregistrer au coffre'}
                </button>
              </form>
            ) : <p className="lc-hint">La gestion des jetons demande la permission d’administration Communication.</p>}
          </div>
        </section>
      </div>

      {gere ? (
        <section className="lc-panel acq-panel">
          <header className="lc-panel__head"><h2 className="lc-section-title">Diagnostic</h2></header>
          <div className="lc-panel__body">
            <div className="acq-form__actions">
              <button type="button" className="lc-btn" disabled={busy !== null} onClick={async () => {
                setBusy('test'); setTestResultat(null)
                const r = await executerCommande('TEST_CONNECTION', {})
                setBusy(null)
                setTestResultat(r.result ?? { erreur: r.human_error ?? 'échec' })
                toast(r.ok ? 'ok' : 'error', r.ok ? 'Connexion vérifiée.' : (r.human_error ?? 'Test en échec.'))
                charger()
              }}>{busy === 'test' ? 'Test…' : 'Tester'}</button>
              <button type="button" className="lc-btn lc-btn--primary" disabled={busy !== null} onClick={async () => {
                setBusy('sync'); setSyncBilan(null)
                const r = await executerCommande('SYNC_NOW', {})
                setBusy(null)
                setSyncBilan(r.result ?? { erreur: r.human_error ?? 'échec' })
                toast(r.ok ? 'ok' : 'error', r.ok ? 'Synchronisation terminée.' : (r.human_error ?? 'Synchronisation en échec.'))
                charger()
              }}>
                <RefreshCw size={14} aria-hidden="true" className={busy === 'sync' ? 'cm-spin' : ''} />
                {busy === 'sync' ? 'Synchronisation…' : 'Synchroniser maintenant'}
              </button>
            </div>
            {testResultat ? (
              <ul className="cm-actionlist acq-testres">
                <LigneEtat label="Santé">{String((testResultat['health'] as { status?: string } | undefined)?.status ?? testResultat['erreur'] ?? '—')}</LigneEtat>
                <LigneEtat label="Lecture">{testResultat['read_ok'] === true ? 'OK' : testResultat['read_ok'] === false ? 'refusée' : '—'}</LigneEtat>
                <LigneEtat label="Liens visibles">{testResultat['links_count'] !== null && testResultat['links_count'] !== undefined ? String(testResultat['links_count']) : '—'}</LigneEtat>
                <LigneEtat label="Clics annoncés">{testResultat['provider_clicks'] !== null && testResultat['provider_clicks'] !== undefined ? nombreFr(Number(testResultat['provider_clicks'])) : '—'}</LigneEtat>
                <LigneEtat label="Jeton d’administration">{testResultat['write_token_configured'] ? 'configuré (vérifié lors d’une vraie action)' : 'absent'}</LigneEtat>
              </ul>
            ) : null}
            {syncBilan && syncBilan['events'] ? (() => {
              const ev = syncBilan['events'] as Record<string, number>
              const li = syncBilan['links'] as Record<string, number> | undefined
              return (
                <ul className="cm-actionlist acq-testres">
                  <LigneEtat label="Événements reçus">{nombreFr(ev['events_received'] ?? 0)}</LigneEtat>
                  <LigneEtat label="Nouveaux">{nombreFr(ev['inserted'] ?? 0)}</LigneEtat>
                  <LigneEtat label="Doublons ignorés">{nombreFr(ev['duplicates'] ?? 0)}</LigneEtat>
                  <LigneEtat label="Robots">{nombreFr(ev['bots'] ?? 0)}</LigneEtat>
                  <LigneEtat label="Liens">{li ? nombreFr(li['liens'] ?? 0) : '—'}</LigneEtat>
                  <LigneEtat label="Durée">{ev['duration_ms'] !== undefined ? `${nombreFr(ev['duration_ms'])} ms` : '—'}</LigneEtat>
                  <LigneEtat label="Curseur">{`${ev['cursor_before']} → ${ev['cursor_after']}`}</LigneEtat>
                </ul>
              )
            })() : syncBilan ? <p className="lc-hint">{String(syncBilan['erreur'] ?? '')}</p> : null}
          </div>
        </section>
      ) : null}
    </>
  )
}

export function CommTrackerIntegrationPage() {
  const meta = useShellMeta()
  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <Link className="lc-crumb" href="/settings/integrations">← Intégrations</Link>
        <ToastProvider><Contenu /></ToastProvider>
      </main>
    </Shell>
  )
}
